import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CorpStructNodeItem } from '../../../../models/api/reports/location-filter';
import { FilterCriteria } from '../../../../models/api/reports/filter-detail-response';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { CheckboxComponent } from '../../../core/controls/checkbox.component';
import { RadioButtonComponent } from '../../../core/controls/radio-button.component';
import {
  Operator,
  OperatorInputComponent,
} from '../../../core/controls/operator-input.component';
import { DialogComponent } from '../../../core/panels/dialog.component';
import { LoadingPanelComponent } from '../../../core/panels/loading-panel.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { CorporateStructureComponent } from '../../../core/controls/corp-struct-selector/corp-structs.component';
import { CorpStruct } from '../../../core/controls/corp-struct-selector/corp-struct-selector.component';
import { TreeComponent, TreeNode } from '../../../core/tree/tree.component';
import { MenuComponent } from '../../../core/panels/menu.component';
import { Action } from '../../../../models/core/action';

@Component({
  selector: 'app-corporate-structure-criteria',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    CheckboxComponent,
    RadioButtonComponent,
    DialogComponent,
    LoadingPanelComponent,
    ActionRowComponent,
    CorporateStructureComponent,
    TreeComponent,
    MenuComponent,
    OperatorInputComponent,
  ],
  template: `
    <div class="tw-flex tw-flex-col tw-gap-4">
      <app-action-row>
        <div left1>
          <app-button
            (click)="corpStructMenu.toggle($event)"
            emphasis="Low"
            icon="fa-light fa-ellipsis-vertical"
            label="Add corporate structure"
          />
        </div>
        <div right1 class="tw-flex tw-items-center">
          <app-radio-button
            [control]="includeTypeControl"
            screenReaderLabel="Selection type"
            optionLabel="name"
            optionValue="code"
            [options]="includeOptions"
            [showHelpText]="false"
            [vertical]="false"
          />
        </div>
      </app-action-row>

      <app-corp-structs
        [items]="selectedCorpStructs"
        [showDeleteColumn]="true"
        (itemsChanged)="onCorpStructsChanged($event)"
      />
    </div>

    <app-dialog
      #corpStructSearchDialog
      [closable]="false"
      label="Search corporate structure"
      [width]="'60rem'"
      [focusOnShow]="false"
      [initialFocusSelector]="'input'"
    >
      <ng-template #content>
        <form [formGroup]="corpSearchForm" class="tw-py-2">
          <div class="tw-flex tw-gap-2 tw-items-center">
            <div class="tw-basis-1/2">
              <app-operator-input
                [control]="corpSearchForm.controls.name"
                [operatorControl]="corpSearchForm.controls.nameOp"
                [operators]="nameOperators"
                label="Name"
                [showHelpText]="false"
              />
            </div>
            <app-button
              emphasis="Low"
              left1
              label="Search"
              type="submit"
              [loading]="searchingCorpStruct"
              (click)="searchCorpStruct()"
            />
            <div class="tw-grow tw-flex tw-flex-row-reverse">
              <app-checkbox
                [control]="corpSearchForm.controls.includeIntermediate"
                label="Include intermediate levels"
                [showHelpText]="false"
              />
            </div>
          </div>
        </form>

        <div class="tw-pb-8 tw-pt-4">
          <app-loading-panel
            loadingStyle="Box"
            [state]="searchingCorpStruct ? 'Loading' : 'Loaded'"
          >
            <ng-template #loaded>
              <app-corp-structs
                #searchCorpStructsTable
                [items]="corpSearchResults"
                [showDeleteColumn]="false"
                paginatorDropdownAppendTo="self"
                (selected)="onCorpStructSearchSelectionChanged($event)"
              />
            </ng-template>
          </app-loading-panel>
        </div>

        <app-action-row>
          <app-button
            [disabled]="!hasCorpStructSearchSelection"
            [badgeLabel]="corpStructSearchSelectionCount || null"
            right1
            label="Add"
            (click)="addSelectedCorpStructsFromSearch()"
          />
          <app-button
            right2
            emphasis="Low"
            label="Close"
            (click)="closeCorpSearchDialog()"
          />
        </app-action-row>
      </ng-template>
    </app-dialog>

    <app-dialog
      #corpStructBrowseDialog
      [closable]="false"
      label="Browse corporate structures"
    >
      <ng-template #content>
        <app-loading-panel
          loadingStyle="Box"
          [state]="loadingTree() ? 'Loading' : 'Loaded'"
        >
          <ng-template #loaded>
            <app-tree
              [dataSource]="treeNodes()"
              (select)="onTreeSelect($event)"
              (unSelect)="onTreeSelect($event)"
              (expand)="onTreeExpand($event)"
            />
          </ng-template>
        </app-loading-panel>

        <app-action-row>
          <app-button
            [disabled]="topLevelSelectedCount === 0"
            [badgeLabel]="topLevelSelectedCount || null"
            right1
            label="Add"
            (click)="addSelectedCorpStructsFromBrowse()"
          />
          <app-button
            right2
            emphasis="Low"
            label="Close"
            (click)="closeCorpBrowseDialog()"
          />
        </app-action-row>
      </ng-template>
    </app-dialog>

    <app-menu
      [items]="corpStructActions"
      #corpStructMenu
      (click)="openCorpStructDialog($event)"
    />
  `,
  styles: ``,
})
export class CorporateStructureCriteriaComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  @Input() contractNumber: number;
  @Input() includeType: 'include' | 'exclude' = 'include';
  @Input() initialCriteria: FilterCriteria[] = [];
  @Output() includeTypeChange = new EventEmitter<'include' | 'exclude'>();
  @Output() selectionChange = new EventEmitter<void>();

  includeTypeControl = new FormControl<string>('include');
  includeOptions = [
    { name: 'Include', code: 'include' },
    { name: 'Exclude', code: 'exclude' },
  ];

  nameOperators: Operator[] = [
    { key: 'eq', placeholder: 'Name', label: 'Equals' },
    { key: 'contains', placeholder: 'Name contains', label: 'Contains' },
    { key: 'sw', placeholder: 'Name starts with', label: 'Starts with' },
    { key: 'ew', placeholder: 'Name ends with', label: 'Ends with' },
  ];

  corpStructActions: Action[] = [{ label: 'Search' }, { label: 'Browse' }];

  selectedCorpStructs: CorpStruct[] = [];
  corpSearchResults: CorpStruct[] = [];
  searchingCorpStruct = false;
  loadingTree = signal<boolean>(false);
  treeNodes = signal<TreeNode[]>([]);
  selectedTreeNodes: TreeNode[] = [];
  topLevelSelectedCount = 0;
  private nodeDataMap = new Map<number, CorpStruct>();

  @ViewChild('corpStructSearchDialog') corpStructSearchDialog: DialogComponent;
  @ViewChild('corpStructBrowseDialog') corpStructBrowseDialog: DialogComponent;
  @ViewChild('searchCorpStructsTable')
  searchCorpStructsTable: CorporateStructureComponent;
  hasCorpStructSearchSelection = false;
  corpStructSearchSelectionCount = 0;

  corpSearchForm = new FormGroup({
    name: new FormControl<string>(null),
    nameOp: new FormControl<string>('contains'),
    includeIntermediate: new FormControl<boolean>(false),
  });

  async ngOnInit() {
    this.includeTypeControl.setValue(this.includeType, { emitEvent: false });
    this.dispose.subs = this.includeTypeControl.valueChanges.subscribe(
      (value) => {
        if (value) {
          this.includeTypeChange.emit(value as 'include' | 'exclude');
        }
      },
    );

    if (this.initialCriteria?.length) {
      await this.setCorpStructsFromCriteria(this.initialCriteria);
    }
  }

  private async setCorpStructsFromCriteria(criteria: FilterCriteria[]) {
    const nodeIds: string[] = criteria.flatMap((c) =>
      c.values.map((v) => v.replace('|', '')),
    );
    if (!nodeIds.length || this.contractNumber <= 0) return;

    const response = await this.api.filtersCriteria.getCAUNodeDetails(
      this.contractNumber,
      nodeIds,
    );

    this.selectedCorpStructs = response.nodes.map((n) =>
      this.mapToCorpStruct(n),
    );

    this.selectionChange.emit();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['includeType'] && this.includeTypeControl) {
      this.includeTypeControl.setValue(this.includeType, { emitEvent: false });
    }
  }

  getCriteria(): FilterCriteria[] {
    if (this.selectedCorpStructs.length === 0) return [];

    const isExclude = this.includeTypeControl.value === 'exclude';
    const operator = isExclude ? 'NOT LIKE' : 'LIKE';

    const nodeIds = this.selectedCorpStructs.map((c) => c.node.toString());

    return [
      {
        tableAlias: 'filter_corp_struct_levels',
        column: 'NODE_ID_LIST',
        displayName: '',
        operator,
        values: nodeIds,
        lineOfBusiness: '',
        dataType: 'VARCHAR2',
        isClaimLab: false,
        formattedOperator: '',
        formattedValue: '',
        filterSection: 'Location',
        filterSubsection: 'Corporate Structure',
      } as FilterCriteria,
    ];
  }

  getCount(): number {
    return this.selectedCorpStructs.length;
  }

  clearSelection(): void {
    this.selectedCorpStructs = [];
    if (this.corpStructSearchDialog) {
      this.corpStructSearchDialog.visible = false;
    }
    if (this.corpStructBrowseDialog) {
      this.corpStructBrowseDialog.visible = false;
    }
    this.selectedTreeNodes = [];
  }

  onCorpStructsChanged(corpStructs: CorpStruct[]) {
    this.selectedCorpStructs = corpStructs;
    this.selectionChange.emit();
  }

  onCorpStructSearchSelectionChanged(corpStructs: CorpStruct[]) {
    this.hasCorpStructSearchSelection = corpStructs.length > 0;
    this.corpStructSearchSelectionCount = corpStructs.length;
  }

  openCorpStructDialog(event: Action) {
    if (event.label === 'Browse') {
      this.loadRootNodes();
      this.corpStructBrowseDialog.visible = true;
      this.topLevelSelectedCount = 0;
    } else if (event.label === 'Search') {
      this.corpSearchForm.reset({
        nameOp: 'contains',
        includeIntermediate: false,
      });
      this.corpSearchResults = [];
      this.hasCorpStructSearchSelection = false;
      this.corpStructSearchSelectionCount = 0;
      requestAnimationFrame(() => {
        this.corpStructSearchDialog.visible = true;
      });
    }
  }

  closeCorpSearchDialog() {
    this.corpStructSearchDialog.visible = false;
    this.corpSearchResults = [];
    this.hasCorpStructSearchSelection = false;
    this.corpStructSearchSelectionCount = 0;
  }

  closeCorpBrowseDialog() {
    this.corpStructBrowseDialog.visible = false;
    this.selectedTreeNodes = [];
    this.topLevelSelectedCount = 0;
  }

  async searchCorpStruct() {
    if (this.contractNumber <= 0) return;

    this.searchingCorpStruct = true;
    this.hasCorpStructSearchSelection = false;
    this.corpStructSearchSelectionCount = 0;

    const searchText = this.buildSearchPattern(
      this.corpSearchForm.controls.name.value || '',
      this.corpSearchForm.controls.nameOp.value || 'contains',
    );

    const response = await this.api.filtersCriteria.searchCorpStruct({
      contractNumber: this.contractNumber,
      searchText,
      includeIntermediateLevels:
        this.corpSearchForm.controls.includeIntermediate.value || false,
    });

    this.corpSearchResults = response.nodes.map((n) => this.mapToCorpStruct(n));
    this.searchingCorpStruct = false;
  }

  private buildSearchPattern(searchText: string, operator: string): string {
    if (!searchText) return '%';

    switch (operator) {
      case 'eq':
        return searchText;
      case 'contains':
        return `%${searchText}%`;
      case 'sw':
        return `${searchText}%`;
      case 'ew':
        return `%${searchText}`;
      default:
        return `%${searchText}%`;
    }
  }

  addSelectedCorpStructsFromSearch() {
    if (!this.searchCorpStructsTable) return;

    const selectedItems = this.searchCorpStructsTable.getSelectedItems();
    if (selectedItems.length === 0) return;

    const existingIds = new Set(
      this.selectedCorpStructs.map((c) => c.corpStructId),
    );

    for (const item of selectedItems) {
      if (!existingIds.has(item.corpStructId)) {
        this.selectedCorpStructs.push({ ...item });
        existingIds.add(item.corpStructId);
      }
    }

    this.selectedCorpStructs = [...this.selectedCorpStructs];
    this.searchCorpStructsTable.clearSelection();
    this.hasCorpStructSearchSelection = false;
    this.corpStructSearchSelectionCount = 0;
    this.corpStructSearchDialog.visible = false;
    this.selectionChange.emit();
  }

  async loadRootNodes() {
    if (this.contractNumber <= 0) return;

    this.loadingTree.set(true);

    const response = await this.api.filtersCriteria.getCorpStruct({
      contractNumber: this.contractNumber,
      parentNodeId: 0,
    });

    this.treeNodes.set(response.nodes.map((n) => this.mapToTreeNode(n, null)));
    this.loadingTree.set(false);
  }

  async onTreeExpand(node: TreeNode) {
    if (node.loaded || this.contractNumber <= 0) return;

    const response = await this.api.filtersCriteria.getCorpStruct({
      contractNumber: this.contractNumber,
      parentNodeId: node.value as number,
    });

    node.children = response.nodes.map((n) => this.mapToTreeNode(n, node));
    node.loaded = true;
    node.loading = false;

    this.treeNodes.set([...this.treeNodes()]);
  }

  onTreeSelect(event: TreeNode | TreeNode[]) {
    this.selectedTreeNodes = Array.isArray(event)
      ? event
      : event
        ? [event]
        : [];

    if (this.selectedTreeNodes.length === 0) {
      this.topLevelSelectedCount = 0;
    } else {
      const selectedValues = new Set(
        this.selectedTreeNodes.map((n) => n.value),
      );
      this.topLevelSelectedCount = this.selectedTreeNodes.filter(
        (node) => !node.parent || !selectedValues.has(node.parent.value),
      ).length;
    }
  }

  addSelectedCorpStructsFromBrowse() {
    if (this.selectedTreeNodes.length === 0) return;

    const selectedValues = new Set(this.selectedTreeNodes.map((n) => n.value));

    const topLevelSelectedNodes = this.selectedTreeNodes.filter(
      (node) => !node.parent || !selectedValues.has(node.parent.value),
    );

    const existingIds = new Set(
      this.selectedCorpStructs.map((c) => c.corpStructId),
    );

    for (const treeNode of topLevelSelectedNodes) {
      const nodeId = treeNode.value as number;
      const data = this.nodeDataMap.get(nodeId);

      if (data && !existingIds.has(data.corpStructId)) {
        this.selectedCorpStructs.push({ ...data });
        existingIds.add(data.corpStructId);
      }
    }

    this.selectedCorpStructs = [...this.selectedCorpStructs];
    this.selectedTreeNodes = [];
    this.topLevelSelectedCount = 0;
    this.corpStructBrowseDialog.visible = false;
    this.selectionChange.emit();
  }

  private mapToCorpStruct(node: CorpStructNodeItem): CorpStruct {
    const corpStruct = new CorpStruct();
    corpStruct.corpStructId = `node-${node.nodeId}`;
    corpStruct.name = node.locationName || '';
    corpStruct.accountNumber = node.accountNumber ?? null;
    corpStruct.unitNumber = node.unitNumber || null;
    corpStruct.code = node.locCode || null;
    corpStruct.node = node.nodeId;
    return corpStruct;
  }

  private mapToTreeNode(
    node: CorpStructNodeItem,
    parent: TreeNode | null,
  ): TreeNode {
    const corpStruct = this.mapToCorpStruct(node);
    this.nodeDataMap.set(node.nodeId, corpStruct);

    const treeNode = new TreeNode();
    treeNode.label = node.locationName || '';
    treeNode.value = node.nodeId;
    treeNode.checked = false;
    treeNode.selected = false;
    treeNode.loaded = !node.hasChildren;
    treeNode.parent = parent;
    treeNode.children = node.hasChildren ? undefined : [];
    return treeNode;
  }
}
